import React, { useEffect, useState } from 'react';
import throttle from 'lodash/throttle';
import { Link } from 'gatsby';
import cx from 'classnames';
import Social from '../social';
import styles from './nav.module.css';
import { defaultMenu } from './menu';

interface Props {
  isRed?: boolean;
  isBanner?: boolean;
}

interface LinkControl {
  title: string;
}

interface LinkAPI {
  active?: string;
  setActive(a: string | undefined): void;
}

interface LinkItem extends LinkControl {
  to: string;
}

interface LinkList extends LinkControl {
  list: ReadonlyArray<LinkItem>;
}

type ItemProps = (LinkItem & LinkAPI) | (LinkList & LinkAPI);

function Item({ title, active, setActive, ...rest }: ItemProps) {
  if ('list' in rest) {
    return (
      <div className={cx(styles.submenu, { [styles.current]: title === active })}>
        <button
          type="button"
          onClick={() => setActive(active === title ? undefined : title)}
          className={cx(styles.link)}
          style={{ cursor: 'default' }}
        >
          {title}
        </button>

        <ul className={styles.list} itemScope itemType="http://schema.org/SiteNavigationElement">
          {rest.list.map((e) => (
            <li key={e.title} className={styles.item}>
              <Item title={e.title} to={e.to} setActive={() => {}} />
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return (
    <Link className={styles.link} to={rest.to}>
      {title}
    </Link>
  );
}

function Navigation({ isRed, isBanner }: Props) {
  const [isOpen, setOpen] = React.useState(false);
  const [active, setActive] = React.useState<string | undefined>();
  const [isScrolled, setScrolled] = React.useState(false);

  const menu = React.useMemo(() => defaultMenu as (LinkItem | LinkList)[], []);
  const toggleOpen = () => {
    document.body.style.overflowY = !isOpen ? 'hidden' : 'scroll';
    setOpen(!isOpen);
  };

  const handleScroll = throttle(() => {
    if (document.documentElement.scrollTop > 50) {
      setScrolled(true);
    } else {
      setScrolled(false);
    }
  }, 300);

  useEffect(() => {
    setScrolled(document.documentElement.scrollTop > 50);
    window.addEventListener('scroll', handleScroll);
    return () => {
      document.body.style.overflowY = 'scroll';
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div
      className={cx(styles.container, {
        [styles.scrolled]: isScrolled,
        [styles.red]: isRed,
        [styles.lower]: isBanner,
      })}
    >
      <div className={styles.content}>
        <div className={styles.wrapper}>
          <div className={cx(styles.drawer, { [styles.open]: isOpen })}>
            <nav>
              <div className={styles.menu}>
                {menu.map((e) => (
                  <Item key={e.title} setActive={setActive} active={active} {...e} />
                ))}
                <Item setActive={setActive} title="Get In Touch" to="/get-in-touch/" />
              </div>
            </nav>
            <div className={styles.bottom}>
              <Link className={styles.term} to="/">
                Terms & Conditions
              </Link>
              <Link className={styles.term} to="/">
                Privacy Policy
              </Link>
            </div>
            <Social />
          </div>
        </div>
        <div className={cx(styles.top, { [styles.topOpen]: isOpen })}>
          <Link to="/" className={cx(styles.logoLink, { [styles.red]: isRed || isOpen })} />
          <nav className={styles.nav} itemScope itemType="http://schema.org/SiteNavigationElement">
            <div className={styles.menu}>
              {menu.map((e) => (
                <Item key={e.title} setActive={setActive} active={active} {...e} />
              ))}
            </div>
            <Link className={cx(styles.btn)} to="/get-in-touch/">
              Get in touch
            </Link>
          </nav>
          <button
            type="button"
            className={cx(styles.burger, { [styles.open]: isOpen })}
            onClick={toggleOpen}
          >
            <span />
          </button>
        </div>
      </div>
    </div>
  );
}

export default Navigation;
